import type { Keyboard, KeyGeometry } from "@/domain/types";
import { modifierFamily } from "./modifier-family";

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Rotate a point (px,py) by deg degrees around the origin (ox,oy). */
export function rotatePoint(
  px: number,
  py: number,
  deg: number,
  ox: number,
  oy: number,
): [number, number] {
  if (!deg) return [px, py];
  const a = (deg * Math.PI) / 180;
  const dx = px - ox;
  const dy = py - oy;
  return [ox + dx * Math.cos(a) - dy * Math.sin(a), oy + dx * Math.sin(a) + dy * Math.cos(a)];
}

// A real board is at most ~23 key-units wide; bounding the size rejects a corrupt or hostile import
// (a NaN dimension, a runaway coordinate) and keeps assignSplitRegions' 0.05u scanline - which runs
// across the width - from looping for seconds on a multi-million-unit span.
const MAX_BOARD_UNITS = 100;

/**
 * Reject geometry that would otherwise render an invisible/broken board or hang the split scanline.
 * Key x/y may be negative (KLE coordinates before this normalization shifts them), but every
 * coordinate must be finite and each key's w/h a finite positive number, and the board's own span
 * must be finite and within range. The importer is the single source of geometry, so this is the one
 * choke point all three importers pass through before assignSplitRegions.
 */
function assertSaneGeometry(keys: readonly KeyGeometry[], width: number, height: number): void {
  for (const k of keys) {
    const h = k.h ?? 1;
    if (
      !Number.isFinite(k.x) ||
      !Number.isFinite(k.y) ||
      !Number.isFinite(k.w) ||
      !Number.isFinite(h) ||
      k.w <= 0 ||
      h <= 0
    ) {
      throw new Error(
        "This keyboard file has invalid key geometry (a non-finite or non-positive coordinate).",
      );
    }
  }
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0 ||
    width > MAX_BOARD_UNITS ||
    height > MAX_BOARD_UNITS
  ) {
    throw new Error(
      `This keyboard file's dimensions are out of range (${width} x ${height} key-units); its key coordinates look malformed.`,
    );
  }
}

/** Shift keys (and their rotation origins) so the bounding box starts at (0,0); returns the size. */
export function normalizeOrigin(keys: KeyGeometry[]): { width: number; height: number } {
  const b = rotatedBounds(keys);
  const width = +(b.maxX - b.minX).toFixed(3);
  const height = +(b.maxY - b.minY).toFixed(3);
  assertSaneGeometry(keys, width, height);
  for (const k of keys) {
    k.x -= b.minX;
    k.y -= b.minY;
    if (k.r != null) {
      k.rx = (k.rx ?? 0) - b.minX;
      k.ry = (k.ry ?? 0) - b.minY;
    }
  }
  return { width, height };
}

// Split-board detection thresholds, in key units / fractions. Calibrated against every built-in
// board: true splits have a >=0.5u central gap dividing the keys evenly; rectangular boards have at
// most a 0.25u, off-centre numpad gap. The three guards (wide, centred, balanced) each rule the
// rectangular boards out, so the rule is robust without per-board exceptions.
export const SPLIT_MIN_GAP = 0.45;
const SPLIT_CENTER_TOL = 0.1;
const SPLIT_BALANCE_TOL = 0.15;

/** A vertical band of the plate no key's x-extent covers, in units. */
export interface VerticalGap {
  start: number;
  end: number;
  w: number;
}

/**
 * Every internal full-height vertical gap, left to right, via a scanline over the keys. The widest
 * one drives split detection (assignSplitRegions); the narrow ones separate a full-size board's
 * key blocks (main / nav / numpad) and feed the KLE importer's numpad detector.
 */
export function verticalGaps(keys: KeyGeometry[], width: number): VerticalGap[] {
  const STEP = 0.05;
  const gaps: VerticalGap[] = [];
  let open = -1;
  for (let x = 0; x <= width + 1e-9; x += STEP) {
    const covered = keys.some((k) => x >= k.x - 1e-9 && x < k.x + k.w - 1e-9);
    if (!covered) {
      if (open < 0) open = x;
    } else if (open >= 0) {
      gaps.push({ start: open, end: x, w: x - open });
      open = -1;
    }
  }
  return gaps;
}

/**
 * Tag each key's region "L"/"R" when the board is a two-piece split, so the two halves render on
 * separate deck plates (P1). The importer owns geometry, so the split is read from the
 * geometry, never hand-marked: a board splits when its widest internal vertical gap is wide, centred
 * on the board, and divides the keys into balanced halves - true of every split/ergo board and of
 * none of the rectangular ones. Mutates and returns `keys`; a no-op (regions untouched) otherwise.
 * `width` is the board width in units (post-normalizeOrigin).
 */
export function assignSplitRegions(keys: KeyGeometry[], width: number): KeyGeometry[] {
  if (keys.length < 8) return keys;
  // the widest internal vertical band (no key's x-extent covers it)
  let best: VerticalGap = { start: 0, end: 0, w: 0 };
  for (const gap of verticalGaps(keys, width)) if (gap.w > best.w) best = gap;
  if (best.w < SPLIT_MIN_GAP) return keys;
  const gapCenter = (best.start + best.end) / 2;
  if (Math.abs(gapCenter - width / 2) > SPLIT_CENTER_TOL * width) return keys;
  const left = keys.filter((k) => k.x + k.w / 2 < gapCenter).length;
  if (Math.abs(left - (keys.length - left)) > SPLIT_BALANCE_TOL * keys.length) return keys;
  for (const k of keys) k.region = k.x + k.w / 2 < gapCenter ? "L" : "R";
  return keys;
}

/**
 * Whether a board renders as a two-piece split: true iff assignSplitRegions tagged any key with a
 * region. The split is read from geometry, never a hand-set flag, so the keyboard picker
 * can section split boards from rectangular ones without per-board metadata.
 */
export function isSplitKeyboard(kb: Keyboard): boolean {
  return kb.keys.some((k) => k.region != null);
}

/** Index into a Keyboard's keys of the hand-side modifier cluster the Plus remap relocates. */
export interface ModifierCluster {
  shift: number;
  ctrl: number;
  alt: number;
  /** The key next to Control on the spacebar side (the Menu/App key right, the Win/Meta key left). */
  neighbor: number;
}

// The neighbor-of-Control the remap turns into Shift is a GUI/Menu key ("the Menu/
// Application key on the right, typically the Win/Meta key on the left"). Requiring that family is
// what gates ergo/thumb-cluster boards cleanly - they have no such key next to Control, so Plus
// annotates rather than mislabelling a stray neighbor (End, a second Alt) as Shift.
const isGuiNeighbor = (code: string): boolean =>
  code.startsWith("Meta") || code.startsWith("OS") || code === "ContextMenu";

// A far GUI key across a wide gap is not "next to" Control; cap the centre-to-centre reach.
const NEIGHBOR_MAX_GAP = 2.5;

/**
 * Locate the hand-side modifier cluster the Plus derived board layer relocates: the side's
 * outermost Shift / Control / Alt and the GUI/Menu key next to Control. The side is read from GEOMETRY
 * (which side of the spacebar centre, robust to a numpad that skews kb.width), never from the code,
 * because a universal board reports both physical sides with the same *Left code. Returns
 * null when the side lacks any of the four - the board is Plus-incompatible and must gate/annotate.
 */
export function findModifierCluster(kb: Keyboard, hand: "left" | "right"): ModifierCluster | null {
  const space = kb.keys.find((k) => k.code === "Space");
  const mid = space ? space.x + space.w / 2 : kb.width / 2;
  const center = (k: KeyGeometry) => k.x + k.w / 2;
  const onSide = (k: KeyGeometry) => (hand === "right" ? center(k) > mid : center(k) < mid);
  // the OUTERMOST key of a family on the hand side: rightmost on the right hand, leftmost on the left
  const pick = (fam: "ctrl" | "alt" | "shift"): number => {
    let bestIdx = -1;
    let bestX = 0;
    kb.keys.forEach((k, i) => {
      if (modifierFamily(k.code) !== fam || !onSide(k)) return;
      if (bestIdx < 0 || (hand === "right" ? k.x > bestX : k.x < bestX)) {
        bestIdx = i;
        bestX = k.x;
      }
    });
    return bestIdx;
  };
  const shift = pick("shift");
  const ctrl = pick("ctrl");
  const alt = pick("alt");
  const ctrlKey = kb.keys[ctrl];
  if (shift < 0 || ctrl < 0 || alt < 0 || !ctrlKey) return null;
  let neighbor = -1;
  let bestGap = Number.POSITIVE_INFINITY;
  // "next to Control" must mean Control's own side: on a split board that is its half's region tag
  // (the spacebar-side test misclassifies keys near the gap when Space sits on one thumb cluster),
  // elsewhere the spacebar side. Without this the left-hand scan crosses the Kinesis bowl and grabs
  // the RIGHT half's Meta as the "neighbor".
  const sameSideAsCtrl = (k: KeyGeometry): boolean =>
    ctrlKey.region != null || k.region != null ? k.region === ctrlKey.region : onSide(k);
  kb.keys.forEach((k, i) => {
    if (i === ctrl || !isGuiNeighbor(k.code) || Math.abs(k.y - ctrlKey.y) > 0.5) return; // GUI key, same row
    if (!sameSideAsCtrl(k)) return;
    const inner = hand === "right" ? center(k) < center(ctrlKey) : center(k) > center(ctrlKey);
    if (!inner) return;
    const gap = Math.abs(center(k) - center(ctrlKey));
    if (gap < bestGap) {
      bestGap = gap;
      neighbor = i;
    }
  });
  if (neighbor < 0 || bestGap > NEIGHBOR_MAX_GAP) return null;
  return { shift, ctrl, alt, neighbor };
}

/**
 * The pad (px) a split half's deck plate may extend toward its sibling, given the gap (units)
 * between the two halves' bounding boxes. A full pad on each facing edge collides when the gap is
 * narrow (ErgoDox/Redox leave ~0.3u once their inward-angled thumbs are counted). Capping the inner
 * pad to keep a channel guarantees the plates never overlap: the separation is `gapUnits*unit -
 * 2*pad >= min(gapPx/2, pad) > 0` for any positive gap. A wide gap returns the full `pad` unchanged.
 */
export function splitPlatePad(gapUnits: number, unit: number, pad: number): number {
  const gapPx = Math.max(0, gapUnits * unit);
  const channel = Math.min(pad, gapPx * 0.5);
  return Math.max(0, Math.min(pad, (gapPx - channel) / 2));
}

/** Axis-aligned bounding box of a set of keys, accounting for each key's rotation. Reads only
 * geometry, so it also accepts rendered caps (whose active code may be null). */
export function rotatedBounds(keys: readonly Omit<KeyGeometry, "code">[]): Bounds {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const k of keys) {
    const h = k.h || 1;
    const corners: Array<[number, number]> = [
      [k.x, k.y],
      [k.x + k.w, k.y],
      [k.x + k.w, k.y + h],
      [k.x, k.y + h],
    ];
    for (const [cx, cy] of corners) {
      const [px, py] = k.r ? rotatePoint(cx, cy, k.r, k.rx ?? 0, k.ry ?? 0) : [cx, cy];
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px);
      maxY = Math.max(maxY, py);
    }
  }
  return { minX, minY, maxX, maxY };
}
